// Celsius <-> Kelvin Calculator tool: unit metadata + client-side field validation, used
// for instant inline feedback as the user types. The actual conversion math and the
// authoritative (server-side) validation live in the main backend — see
// src/lib/toolsApi.js (temperatureToolApi) and backend/tools/temperature_tool.py.

export const DEFAULT_INPUTS = {
  unit: 'celsius',
  value: 20,
};

// Celsius is the canonical/base unit — every other unit's offset below is expressed
// relative to it. Order here drives the order results are displayed in.
export const UNITS = [
  { value: 'celsius', label: '°C', name: 'Celsius' },
  { value: 'kelvin', label: 'K', name: 'Kelvin' },
  { value: 'fahrenheit', label: '°F', name: 'Fahrenheit' },
];

// Absolute zero, expressed in each unit's own scale. Unlike every other converter in
// this codebase (kW/HP, torque, pressure, speed) the floor is NOT one fixed number
// shared across units, because temperature conversion is offset-based, not
// multiplicative — 0 K, -273.15 °C, and -459.67 °F all name the same physical
// temperature. Mirrors ABSOLUTE_ZERO in backend/core/lookup/temperature_specs.py.
export const ABSOLUTE_ZERO = {
  celsius: -273.15,
  kelvin: 0,
  fahrenheit: -459.67,
};

// No static `min` here (unlike every other tool's FIELD_SPECS) — the floor depends on
// the selected unit and is enforced via ABSOLUTE_ZERO in validateField() below.
// Mirrors FIELD_SPECS in backend/core/lookup/temperature_specs.py exactly.
export const FIELD_SPECS = [
  { key: 'value', label: 'Value', unit: '', group: 'input', min: null, max: 1000000, strictlyPositive: false },
];

// Task spec: 1-2 decimal places is sufficient for temperature (avoid over-precision) —
// default is lower than the other converters' 4, though the same 2-6 range is kept for
// UI consistency.
export const PRECISION_OPTIONS = [2, 3, 4, 5, 6];
export const DEFAULT_PRECISION = 2;

// `unit` selects which absolute-zero floor applies — unlike the other converters'
// validateField(spec, rawValue), temperature validation is unit-dependent so the
// selected unit must be passed in.
export function validateField(spec, rawValue, unit) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  const floor = ABSOLUTE_ZERO[unit];
  if (floor !== undefined && value < floor) {
    const unitInfo = UNITS.find((u) => u.value === unit);
    return `Value below absolute zero (${floor}${unitInfo ? unitInfo.label : ''}).`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}
